import React from "react";

/**
 * The logo, as components rather than image files, so it inherits the
 * theme's colours and stays crisp at any size.
 *
 * The rule, taken from the app icon: one colour carries the structure —
 * card outline, frank ring, flagstick — and red is reserved for the flag
 * alone. That keeps red as an accent rather than half the logo.
 *
 * Two marks by design: the full card-and-frank above about 40px, and the
 * frank alone below, where the card's grid fills in.
 */

const FLAG = "var(--red)";

export function FullMark({ h = 40, ink = "var(--text)", halo = "var(--page)" }) {
  // viewBox fitted to the real bounds: the frank reaches y=56.5, so the
  // old 0 12 64 42 box was clipping the bottom of the circle.
  return (
    <svg viewBox="2 15 60 43" height={h} width={h * (60 / 43)} aria-hidden="true">
      <rect x="5" y="18" width="42" height="29" rx="2.5" fill="none" stroke={ink} strokeWidth="3.4" />
      <path d="M5 27h42" stroke={ink} strokeWidth="3" />
      <path d="M24 27v20" stroke={ink} strokeWidth="3" />
      <circle cx="46" cy="42" r="14.5" fill={halo} />
      <circle cx="46" cy="42" r="12.5" fill="none" stroke={ink} strokeWidth="3.2" />
      <line x1="42.5" y1="35.5" x2="42.5" y2="48.5" stroke={ink} strokeWidth="2.7" strokeLinecap="round" />
      <path d="M42.5 35 L52 38.3 L42.5 41.6 Z" fill={FLAG} />
    </svg>
  );
}

export function Frank({ s = 28, ink = "var(--text)", halo = "none" }) {
  return (
    <svg viewBox="30 26 32 32" width={s} height={s} aria-hidden="true">
      <circle cx="46" cy="42" r="14.5" fill={halo} />
      <circle cx="46" cy="42" r="12.5" fill="none" stroke={ink} strokeWidth="3.2" />
      <line x1="42.5" y1="35.5" x2="42.5" y2="48.5" stroke={ink} strokeWidth="2.7" strokeLinecap="round" />
      <path d="M42.5 35 L52 38.3 L42.5 41.6 Z" fill={FLAG} />
    </svg>
  );
}

/**
 * One colour for the whole name — navy on light, white on navy — with the
 * o in Golf red, as a ball. No full stop: the domain is postcardgolf.app, so
 * the name is one word, with the capital G doing the separating. No greys: the only thing that stands apart is
 * the ball, which is the point.
 */
export function Wordmark({ size = "1.15rem", ink = "var(--text)" }) {
  return (
    <span style={{ fontSize: size, fontWeight: 700, letterSpacing: "-.025em",
                   color: ink, lineHeight: 1, whiteSpace: "nowrap" }}>
      PostcardG<span style={{ color: "var(--red)" }}>o</span>lf
    </span>
  );
}

/**
 * Optical centring.
 *
 * Aligning the text to the card's geometric centre over-corrects, because
 * the frank is a lot of ink sitting low and to the right — it pulls the
 * eye with it. These figures come from measuring the ink centroid of a
 * rendered mark rather than from the outline coordinates:
 *
 *            geometric (card)   ink centroid   viewBox centre
 *   across         26.0            30.69            32.0
 *   down           32.5            34.25            36.5
 *
 * So the wordmark lifts by 2.25/43 of the height beside the mark, and
 * shifts left by 1.31/43 beneath it — roughly a third of what the
 * geometric centres would suggest.
 */
export const CARD_OFFSET = 2.25 / 43;    // 0.0523 — worth doing, it's visible
export const CARD_SHIFT  = 0;            // measured at 2% of the width: not worth it

/**
 * Horizontal lockup: full mark plus wordmark, the text aligned to the
 * middle of the card rather than the middle of the mark.
 */
export function LockupWide({ h = 44, ink = "var(--text)", halo, wordSize }) {
  const size = wordSize ?? `${(h * 0.46) / 16}rem`;
  // The frank already overhangs the card, so a chunk of the apparent gap is
  // inside the mark's own box. Scale the remainder to the mark's height
  // rather than fixing it in rem, or it drifts as the logo grows.
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: `${h * 0.09}px` }}>
      <FullMark h={h} ink={ink} halo={halo} />
      <span style={{ display: "inline-block", transform: `translateY(${-h * CARD_OFFSET}px)` }}>
        <Wordmark size={size} ink={ink} />
      </span>
    </span>
  );
}

/**
 * Stacked lockup. Here the same problem runs the other way: the frank
 * sticks out to the right, so centring the wordmark under the whole mark
 * pushes it right of the card. It is centred under the card instead.
 */
export function LockupStacked({ h = 52, ink = "var(--text)", halo,
                                wordSize = "1.5rem", align = "center", strap }) {
  const shift = align === "center" ? -h * CARD_SHIFT : 0;
  return (
    <span style={{ display: "inline-flex", flexDirection: "column",
                   alignItems: align === "center" ? "center" : "flex-start",
                   gap: `${h * 0.12}px` }}>
      <FullMark h={h} ink={ink} halo={halo} />
      {/* wordmark and strapline shift together, or they end up centred on
          two different things */}
      <span style={{ display: "inline-flex", flexDirection: "column",
                     alignItems: align === "center" ? "center" : "flex-start",
                     gap: `${h * 0.1}px`, transform: `translateX(${shift}px)` }}>
        <Wordmark size={wordSize} ink={ink} />
        {strap && (
          <span style={{ fontSize: ".6875rem", fontWeight: 700, letterSpacing: ".2em",
                         textTransform: "uppercase", color: "var(--dim)",
                         whiteSpace: "nowrap",
                         // letter-spacing adds a space after the last character
                         // as well, so centred tracked text sits half a space
                         // left of true centre. Take it back.
                         marginRight: "-.2em" }}>
            {strap}
          </span>
        )}
      </span>
    </span>
  );
}

/** Compact lockup: frank plus wordmark. What the rail and top bar use.
 *  The frank is symmetrical, so plain centring is correct here. */
export default function Logo({ size = 28, ink = "var(--text)", halo, wordSize = "1.15rem" }) {
  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: `${size * 0.2}px` }}>
      <Frank s={size} ink={ink} halo={halo} />
      <Wordmark size={wordSize} ink={ink} />
    </span>
  );
}
